import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { ArrowLeft, Check, Clapperboard, Lock, LogIn } from 'lucide-react';
import { AD_GOLD } from '../../shared/economy.ts';
import { COSMETICS, DEFAULT_LOOK, owns, type Cosmetic, type CosmeticKind, type Look } from '../../shared/cosmetics.ts';
import type { InventoryResponse } from '../../shared/protocol.ts';
import { useAuth } from '../app/auth.ts';
import { useT } from '../app/preferences.ts';
import { ConfirmDialog } from '../components/Dialog.tsx';
import { Link } from '../components/Link.tsx';
import { CosmeticPreview, GoldAmount } from '../components/PlayerLook.tsx';
import { useCosmeticName } from '../app/cosmeticName.ts';
import { Segmented } from '../components/Segmented.tsx';
import { useTransient } from '../hooks/useGameView.ts';
import { buyItem, fetchInventory, saveLook } from '../online/progressionApi.ts';
import { watchAd } from '../online/rewardedAd.ts';
import { adsAvailable, prepareAds } from '../lib/ads.ts';

const TABS: { kind: CosmeticKind; label: 'shop.avatars' | 'shop.frames' | 'shop.names'; key: keyof Look }[] = [
  { kind: 'avatar', label: 'shop.avatars', key: 'avatar' },
  { kind: 'frame', label: 'shop.frames', key: 'frame' },
  { kind: 'nameStyle', label: 'shop.names', key: 'nameStyle' },
];

const RARITY_ORDER = { common: 0, rare: 1, epic: 2, legendary: 3 } as const;

export function MarketPage() {
  const t = useT();
  const { user, canSignIn, signIn, refresh } = useAuth();
  const [tab, setTab] = useState<CosmeticKind>('avatar');
  const [inventory, setInventory] = useState<InventoryResponse | null>(null);
  const [confirm, setConfirm] = useState<Cosmetic | null>(null);
  const [notice, setNotice] = useState<{ text: string; at: number } | null>(null);
  const toast = useTransient(notice, 2600);
  const signedIn = Boolean(user?.username);
  const itemName = useCosmeticName();
  /** What an ad is playing for right now ('gold' or an item id); buttons wait meanwhile. */
  const [watching, setWatching] = useState<string | null>(null);

  useEffect(() => {
    if (signedIn) void fetchInventory().then(setInventory);
  }, [signedIn]);

  useEffect(() => {
    if (signedIn) prepareAds();
  }, [signedIn]);

  const owned = new Set(inventory?.owned ?? []);
  const gold = inventory?.gold ?? user?.gold ?? 0;
  const look = inventory?.look ?? user?.look ?? DEFAULT_LOOK;
  const name = user?.username ?? t('online.guest');
  const { key } = TABS.find((x) => x.kind === tab)!;

  // Ad-only items sit among the others at their gold worth; box-only items go last.
  const worth = (c: Cosmetic) => c.price ?? (c.ads ? c.ads * AD_GOLD : Infinity);
  const items = COSMETICS.filter((c) => c.kind === tab && c.source !== 'starter').sort(
    (a, b) => worth(a) - worth(b) || RARITY_ORDER[a.rarity] - RARITY_ORDER[b.rarity],
  );

  const say = (text: string) => setNotice({ text, at: Date.now() });

  const buy = useCallback(
    async (item: Cosmetic) => {
      setConfirm(null);
      const result = await buyItem(item.id);
      if (result.ok) {
        setInventory(result.data);
        say(t('shop.bought'));
        void refresh();
      } else {
        say(t(result.error === 'not_enough_gold' ? 'shop.notEnough' : 'error.generic'));
      }
    },
    [t, refresh],
  );

  const watch = async (purpose: 'gold' | Cosmetic) => {
    const key = purpose === 'gold' ? 'gold' : purpose.id;
    setWatching(key);
    const result = await watchAd(key);
    setWatching(null);
    if (!result.ok) {
      say(t(result.error === 'dismissed' ? 'ads.dismissed' : result.error === 'unavailable' ? 'ads.unavailable' : 'error.generic'));
      return;
    }
    setInventory(result.data);
    void refresh();
    if (result.data.goldEarned > 0) say(t('ads.earned', { gold: result.data.goldEarned }));
    else if (result.data.unlocked) say(t('ads.unlocked', { name: itemName(result.data.unlocked) }));
    else if (purpose !== 'gold' && purpose.ads) {
      say(t('ads.progress', { done: result.data.adProgress[purpose.id] ?? 0, total: purpose.ads }));
    }
  };

  const use = async (item: Cosmetic) => {
    const result = await saveLook({ ...look, [key]: item.id } as Look);
    if (result.ok) {
      setInventory(result.data);
      void refresh();
    }
  };

  return (
    <div className="page-fill contained">
      <div className="page-top">
        <Link href="/" className="back-link">
          <ArrowLeft size={22} strokeWidth={2.5} /> {t('nav.home')}
        </Link>
        <h1 className="page-title">🛍️ {t('shop.title')}</h1>
        {signedIn && (
          <span className="badge badge-accent shop-gold">
            <GoldAmount amount={gold} />
          </span>
        )}
      </div>

      {!signedIn && (
        <div className="banner" style={{ marginBottom: 'var(--gap)' }}>
          <span>{t('shop.signIn')}</span>
          {canSignIn && (
            <button type="button" className="btn btn-sm btn-blue" onClick={() => signIn('/market')}>
              <LogIn size={18} /> {t('auth.signInShort')}
            </button>
          )}
        </div>
      )}

      {signedIn && adsAvailable && (
        <div className="ad-offer">
          <span className="emoji" aria-hidden="true">
            🎬
          </span>
          <span className="ad-offer-text">{t('ads.goldOffer', { gold: AD_GOLD })}</span>
          <button type="button" className="btn btn-sm btn-green" disabled={watching !== null} onClick={() => watch('gold')}>
            <Clapperboard size={18} /> {watching === 'gold' ? t('ads.loading') : t('ads.watch')}
          </button>
        </div>
      )}

      <Segmented<CosmeticKind>
        name="shop-tab"
        label={t('shop.title')}
        value={tab}
        onChange={setTab}
        options={TABS.map((x) => ({ value: x.kind, label: t(x.label) }))}
      />

      <ul className={`shop-grid pop-in ordered${tab === 'nameStyle' ? ' names' : ''}`} key={tab}>
        {items.map((item, index) => {
          const has = owns(owned, item.id);
          const using = look[key] === item.id;
          return (
            <li key={item.id} className={`shop-item card rarity-${item.rarity}`} style={{ '--i': index } as CSSProperties}>
              <span className={`rarity-tag rarity-${item.rarity}`}>{t(`rarity.${item.rarity}`)}</span>
              <div className="shop-preview">
                <CosmeticPreview id={item.id} name={name} look={look} size="lg" />
              </div>
              <span className="shop-name">{itemName(item.id)}</span>
              {using ? (
                <span className="btn btn-sm btn-ghost" aria-disabled="true">
                  <Check size={18} /> {t('shop.using')}
                </span>
              ) : has ? (
                <button type="button" className="btn btn-sm btn-green" onClick={() => use(item)}>
                  {t('shop.use')}
                </button>
              ) : item.price === null && !item.ads ? (
                <span className="btn btn-sm btn-ghost shop-box-only" aria-disabled="true">
                  <Lock size={16} /> {t('shop.boxOnly')}
                </span>
              ) : (
                <>
                  {item.price !== null && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={!signedIn}
                      onClick={() => (gold >= item.price! ? setConfirm(item) : say(t('shop.notEnough')))}
                    >
                      <GoldAmount amount={item.price} />
                    </button>
                  )}
                  {item.ads && (
                    <button
                      type="button"
                      className="btn btn-sm btn-blue shop-ad-btn"
                      disabled={!signedIn || !adsAvailable || watching !== null}
                      title={adsAvailable ? undefined : t('ads.soon')}
                      onClick={() => watch(item)}
                    >
                      <Clapperboard size={16} />
                      {watching === item.id
                        ? t('ads.loading')
                        : t('ads.count', { done: inventory?.adProgress[item.id] ?? 0, total: item.ads })}
                    </button>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>

      <ConfirmDialog
        open={confirm !== null}
        title={confirm ? `${itemName(confirm.id)}: ${t('shop.confirmTitle')}` : t('shop.confirmTitle')}
        body={confirm ? t('shop.confirmBody', { price: confirm.price ?? 0, gold }) : ''}
        confirmLabel={t('shop.buy')}
        cancelLabel={t('shop.notNow')}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm && buy(confirm)}
      />

      {toast && (
        <div className="toast-region" aria-live="polite">
          <div className="toast">{toast.text}</div>
        </div>
      )}
    </div>
  );
}
